import * as React from "react";

import {
  CircleX,
  Eye,
  EyeOff,
  type LucideIcon,
  type LucideProps,
} from "lucide-react";
import { cn } from "../../lib/utils.ts";

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  startIcon?: LucideIcon;
  endIcon?: LucideIcon;
  iconProps?: LucideProps;
  classNameInput?: string;
  onClear?: () => void;
  canClear?: boolean;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      classNameInput,
      type,
      startIcon,
      endIcon,
      iconProps = {},
      onClear,
      canClear,
      ...props
    },
    ref,
  ) => {
    const [show, setShow] = React.useState(false);
    const StartIcon = startIcon;
    const EndIcon = endIcon;
    const { className: iconClassName, ...iconRest } = iconProps;

    if (type === "password") {
      return (
        <div className={cn("w-full relative", className)}>
          <div className="absolute left-1.5 top-1/2 transform -translate-y-1/2">
            {StartIcon && (
              <div className="absolute left-[12px] top-1/2 transform -translate-y-1/2">
                <StartIcon
                  size={18}
                  className={cn("text-muted-foreground", iconClassName)}
                  {...iconRest}
                />
              </div>
            )}
          </div>
          <input
            autoComplete="off"
            type={!show ? type : "text"}
            className={cn(
              "flex h-10 w-full rounded-[8px] border border-[#B3B9C4] bg-[white] not-placeholder-shown:border-[#44546F] hover:border-[#44546F] py-2 px-4 text-[#172B4D] text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring focus-visible:ring-offset-0 disabled:cursor-not-allowed disabled:opacity-50",
              startIcon ? "pl-8" : "",
              className,
            )}
            ref={ref}
            {...props}
          />
          <button
            onClick={() => setShow((prev) => !prev)}
            className="absolute right-3 top-1/2 transform -translate-y-1/2"
            type="button"
          >
            {show ? (
              <Eye className="stroke-slate-700/70" size={18} />
            ) : (
              <EyeOff className="stroke-slate-700/70" size={18} />
            )}
          </button>
        </div>
      );
    }

    return (
      <div className={cn("w-full relative", className)}>
        {StartIcon && (
          <div className="absolute left-[12px] top-1/2 transform -translate-y-1/2">
            <StartIcon
              size={18}
              className={cn("text-muted-foreground", iconClassName)}
              {...iconRest}
            />
          </div>
        )}
        <input
          type={type}
          className={cn(
            "flex h-10 w-full rounded-[8px] border border-[#B3B9C4] bg-[white] not-placeholder-shown:border-[#44546F] hover:border-[#44546F] py-2 px-4 text-[#172B4D] text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring focus-visible:ring-offset-0 disabled:cursor-not-allowed disabled:opacity-50",
            startIcon ? "pl-8" : "",
            endIcon ? "pr-8" : "",
            classNameInput,
          )}
          ref={ref}
          {...props}
        />
        {EndIcon && !canClear && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            <EndIcon
              className={cn("text-muted-foreground", iconClassName)}
              {...iconRest}
              size={18}
            />
          </div>
        )}
        {onClear && canClear && (
          <button
            onClick={() => onClear()}
            className="absolute right-3 top-1/2 transform -translate-y-1/2"
            type="button"
          >
            <CircleX className="stroke-slate-700/70" size={18} />
          </button>
        )}
      </div>
    );
  },
);

export { Input };
